import React, { useEffect, useState } from 'react';
import { useLinks } from './useLinks';
import LinktreeImport from '../Linktree/LinktreeImport';
import LinkForm from './LinkForm';
import Modal from '../../Modal';
import { useSectionFeedback, Feedback } from '../Storefront/sections/sectionFeedback';
import StorefrontPreview from '../Storefront/StorefrontPreview';
import './LinkTreeManager.css';
import RegisteredIcon from '../../RegisteredIcon';

function LinkFormModal({ isOpen, title, initialData, onSave, onClose, submitLabel, successMessage }) {
    const [saving, setSaving] = useState(false);
    const { feedback, showFeedback } = useSectionFeedback();

    async function handleSave(payload) {
        setSaving(true);
        const result = await onSave(payload);
        setSaving(false);
        if (result.success) {
            showFeedback('success', successMessage);
            setTimeout(onClose, 800);
        } else {
            showFeedback('error', result.error);
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            size="large"
            className="ltm-form-modal"
        >
            <LinkForm
                initialData={initialData}
                onSave={handleSave}
                onCancel={onClose}
                saving={saving}
                submitLabel={submitLabel}
            />
            <Feedback feedback={feedback} />
        </Modal>
    );
}

function LinkRow({ link, isFirst, isLast, onMoveUp, onMoveDown, onDelete, onEditStart }) {
    const { feedback, showFeedback } = useSectionFeedback();

    async function handleDelete() {
        if (!window.confirm('Delete this link?')) return;
        const result = await onDelete(link.id);
        if (!result.success) showFeedback('error', result.error);
    }

    return (
        <>
            <tr>
                <td data-label="Move">
                    <div className="ltm-row-actions">
                        <button type="button" className="button small" onClick={() => onMoveUp(link.id)} disabled={isFirst}>
                            <i className="fa-solid fa-arrow-up" /> Up
                        </button>
                        <button type="button" className="button small" onClick={() => onMoveDown(link.id)} disabled={isLast}>
                            Down <i className="fa-solid fa-arrow-down" />
                        </button>
                    </div>
                </td>
                <td data-label="Title">{link.title || <em>Untitled</em>}</td>
                <td data-label="URL">
                    <a href={link.url} target="_blank" rel="noreferrer" title={link.url}>
                        {link.url?.substring(0, 40)}{link.url?.length > 40 ? '…' : ''}
                    </a>
                </td>
                <td data-label="Description">
                    {link.description?.substring(0, 80)}{link.description?.length > 80 ? '…' : ''}
                </td>
                <td data-label="Image">
                    {link.image?.cdnPath
                        ? <img src={`${link.image.cdnPath}?w=30&h=30`} alt={link.title} />
                        : link.icon
                            ? <RegisteredIcon value={link.icon} />
                            : <span>-</span>
                    }
                </td>
                <td data-label="Actions">
                    <div className="ltm-row-actions">
                        <button type="button" className="button small" onClick={() => onEditStart(link)}>
                            Edit
                        </button>
                        <button type="button" className="button small danger" onClick={handleDelete}>
                            Delete
                        </button>
                    </div>
                    <Feedback feedback={feedback} />
                </td>
            </tr>
        </>
    );
}

export default function LinkTreeManager({
    storefrontUrl,
    showPreview = true,
    onChanged,
    wrapperElement = 'section',
}) {
    const Wrapper = wrapperElement;
    const { links, loading, loadError, reload, createLink, updateLink, deleteLink, moveLink } = useLinks();
    const [editingLink, setEditingLink] = useState(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [previewVersion, setPreviewVersion] = useState(0);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [showPreviewSavedMessage, setShowPreviewSavedMessage] = useState(false);
    const [previewScale, setPreviewScale] = useState(0.5);

    useEffect(() => {
        function syncPreviewScale() {
            if (typeof window === 'undefined' || !window.matchMedia) return;
            setPreviewScale(window.matchMedia('(max-width: 640px)').matches ? 1 : 0.5);
        }

        syncPreviewScale();
        window.addEventListener('resize', syncPreviewScale);
        return () => window.removeEventListener('resize', syncPreviewScale);
    }, []);

    function markPreviewStale() {
        setPreviewVersion(version => version + 1);
        setShowPreviewSavedMessage(true);
        onChanged?.();
    }

    async function handleImported() {
        await reload({ silent: true });
        markPreviewStale();
    }

    async function handleAdd(payload) {
        const result = await createLink(payload);
        if (result.success) markPreviewStale();
        return result;
    }

    async function handleUpdate(payload) {
        if (!editingLink) return { success: false, error: 'No link selected.' };
        const result = await updateLink(editingLink.id, payload);
        if (result.success) markPreviewStale();
        return result;
    }

    async function handleMove(id, direction) {
        const result = await moveLink(id, direction);
        if (result.success) markPreviewStale();
        return result;
    }

    async function handleDelete(id) {
        const result = await deleteLink(id);
        if (result.success) markPreviewStale();
        return result;
    }

    if (loading) return <p>Loading links&hellip;</p>;
    if (loadError) return <p className="error">Failed to load links: {loadError}</p>;

    return (
        <div className="ltm-root">
            <Wrapper id="links">
                <h2>Custom Link Tree</h2>
                {showPreview && (
                    <p className="subtitle"><a
                        href={storefrontUrl || undefined}
                        aria-disabled={!storefrontUrl}
                        tabIndex={storefrontUrl ? 0 : -1}
                        onClick={(event) => {
                            event.preventDefault();
                            if (!storefrontUrl) return;
                            setShowPreviewSavedMessage(false);
                            setIsPreviewOpen(true);
                        }}
                    >Preview Storefront</a></p>
                )}

                <p>Show large, custom links to your other pages on your landing page.</p>

                {links.length === 0 ? (
                    <p>No links yet. Add your first link below.</p>
                ) : (
                    <div className="tablewrapper">
                        <table className="ltm-table">
                            <thead>
                                <tr>
                                    <th>Move</th>
                                    <th>Title</th>
                                    <th>URL</th>
                                    <th>Description</th>
                                    <th>Image</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {links.map((link, idx) => (
                                    <LinkRow
                                        key={link.id}
                                        link={link}
                                        isFirst={idx === 0}
                                        isLast={idx === links.length - 1}
                                        onMoveUp={id => handleMove(id, 'up')}
                                        onMoveDown={id => handleMove(id, 'down')}
                                        onDelete={handleDelete}
                                        onEditStart={setEditingLink}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div className="ltm-add-row">
                    <button type="button" className="button primary" onClick={() => setIsCreateOpen(true)}>
                        + Add a Link
                    </button>
                    <LinktreeImport onImported={handleImported} />
                </div>
            </Wrapper>
            <LinkFormModal
                isOpen={isCreateOpen}
                title="Add a Link"
                onSave={handleAdd}
                onClose={() => setIsCreateOpen(false)}
                submitLabel="Add Link"
                successMessage="Link added."
            />
            <LinkFormModal
                isOpen={!!editingLink}
                title="Edit Link"
                initialData={editingLink || {}}
                onSave={handleUpdate}
                onClose={() => setEditingLink(null)}
                submitLabel="Save Changes"
                successMessage="Saved."
            />
            {showPreview && (
                <StorefrontPreview
                    isOpen={isPreviewOpen}
                    onClose={() => {
                        setIsPreviewOpen(false);
                        setShowPreviewSavedMessage(false);
                    }}
                    url={storefrontUrl}
                    version={previewVersion}
                    onRefresh={() => setPreviewVersion(version => version + 1)}
                    showSavedMessage={showPreviewSavedMessage}
                    scale={previewScale}
                />
            )}
        </div>
    );
}
